import {
  useAddress,
  useCreateAddress,
  useCreateSite,
  useDeleteOrganisationLogo,
  useOrganisation,
  useUpdateAddress,
  useUpdateOrganisation,
  useUploadOrganisationLogo,
} from '@my/directory-api/hooks';
import {
  AddressInlineEditor,
  Button,
  ErrorBanner,
  FormSection,
  Loading,
  LogoUploader,
  RegionalSettingsInlineEditor,
  TextInput,
  showToast,
} from '@my/ui';
import { useEffect, useState } from 'react';
import {
  EMPTY_ADDRESS_FORM,
  toAddressForm,
  toCreateAddressRequest,
  toCreateSiteRequest,
  toOrganisationForm,
  toUpdateAddressRequest,
  toUpdateOrganisationRequest,
  type AddressForm,
  type OrganisationForm,
} from './lib/organisationForm';
import {
  COUNTRY_OPTIONS,
  currencyLabel,
  DATE_PATTERN_OPTIONS,
  LANGUAGE_OPTIONS,
  TIMEZONE_OPTIONS,
  TIME_PATTERN_OPTIONS,
} from './lib/selectOptions';

/**
 * The organisation panel, as TMS's Settings → Organisation renders it, minus
 * the accounting half.
 *
 * <p>
 * WHAT IS NOT HERE: accounting system, VAT rounding, default payment terms,
 * order reference labels, and invoice &amp; sequence configuration. Those are
 * finance settings that belong beside the screens that consume them — and the
 * sequences in particular are consumed as documents are numbered, which is not
 * a thing this portal does.
 * </p>
 *
 * <p>
 * The base currency is shown but NOT editable. Changing it reshuffles the
 * additional-currency list and the bank accounts denominated in it, so it is a
 * finance operation with consequences in every other app rather than a field.
 * </p>
 */
export function OrganisationPanel({ organisationId }: { organisationId: string }) {
  const orgQuery = useOrganisation(organisationId);

  const addressId = orgQuery.data?.mainOfficeAddressId ?? null;
  const addressQuery = useAddress(addressId);

  const updateOrg = useUpdateOrganisation();
  const createAddress = useCreateAddress();
  const updateAddress = useUpdateAddress();
  const createSite = useCreateSite();
  const uploadLogo = useUploadOrganisationLogo();
  const deleteLogo = useDeleteOrganisationLogo();

  const [form, setForm] = useState<OrganisationForm | null>(null);
  const [address, setAddress] = useState<AddressForm>(EMPTY_ADDRESS_FORM);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (orgQuery.data) setForm(toOrganisationForm(orgQuery.data));
  }, [orgQuery.data]);

  useEffect(() => {
    if (addressQuery.data) setAddress(toAddressForm(addressQuery.data));
    else if (!addressId) setAddress(EMPTY_ADDRESS_FORM);
  }, [addressQuery.data, addressId]);

  if (orgQuery.isPending || !form) return <Loading label="Organisation" />;
  if (orgQuery.isError) {
    return <ErrorBanner title="Failed to load organisation" message={orgQuery.error.message} />;
  }

  const organisation = orgQuery.data;
  const isSaving =
    updateOrg.isPending || createAddress.isPending || updateAddress.isPending || createSite.isPending;
  const isLogoBusy = uploadLogo.isPending || deleteLogo.isPending;

  const setField = <K extends keyof OrganisationForm>(key: K, value: OrganisationForm[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.invoicingName.trim()) return;
    setError(null);

    try {
      let mainOfficeAddressId = organisation.mainOfficeAddressId ?? null;

      if (addressId && addressQuery.data) {
        await updateAddress.mutateAsync(toUpdateAddressRequest(addressQuery.data, address));
      } else if (address.fullAddress.trim() && address.countryIsoCode.trim()) {
        mainOfficeAddressId = await createAddress.mutateAsync(
          toCreateAddressRequest(organisationId, address),
        );
        // Every Address belongs to a Site; creating one without the other
        // leaves a row nothing else in the suite can point at.
        await createSite.mutateAsync(toCreateSiteRequest(organisationId, mainOfficeAddressId));
      }

      await updateOrg.mutateAsync(
        toUpdateOrganisationRequest(organisation, form, mainOfficeAddressId, address.countryIsoCode),
      );

      showToast({ type: 'success', message: 'Organisation updated.' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the organisation.');
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      {error && <ErrorBanner title="Could not save the organisation" message={error} />}

      <FormSection
        title="Organisation Details"
       
        columns={3}
      >
        {/* ── Column 1 — identity and place ──────────────────────────────── */}
        <div className="space-y-3 text-sm">
          <LogoUploader
            label="Organisation Logo"
            value={organisation.logoUrl ?? null}
            onUpload={(file) =>
              uploadLogo.mutate(
                { id: organisationId, file },
                {
                  onSuccess: () => showToast({ type: 'success', message: 'Logo updated.' }),
                  onError: (err) => showToast({ type: 'error', message: err.message }),
                },
              )
            }
            onClear={() =>
              deleteLogo.mutate(
                { id: organisationId },
                {
                  onSuccess: () => showToast({ type: 'success', message: 'Logo removed.' }),
                  onError: (err) => showToast({ type: 'error', message: err.message }),
                },
              )
            }
            isUploading={isLogoBusy}
          />

          <TextInput
            label="Name"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            error={!form.name.trim()}
            errorMessage="A name is required."
          />

          {/* The country lives HERE, not in a field of its own — the
              organisation's own countryIsoCode follows the main office, which
              is what drives its tax schedule. Same as TMS. */}
          <AddressInlineEditor
            label="Address"
            value={address}
            onChange={setAddress}
            countryOptions={COUNTRY_OPTIONS}
            enablePlacesSearch
            hasCodeField
          />
        </div>

        {/* ── Column 2 — how the company is named and registered ─────────── */}
        <div className="space-y-3 text-sm">
          <TextInput
            label="Invoicing Name"
            value={form.invoicingName}
            onChange={(e) => setField('invoicingName', e.target.value)}
            error={!form.invoicingName.trim()}
            errorMessage="An invoicing name is required."
          />
          <TextInput
            label="Tax Number"
            value={form.taxNumber}
            onChange={(e) => setField('taxNumber', e.target.value)}
          />
          <TextInput
            label="Registration Number"
            value={form.registrationNumber}
            onChange={(e) => setField('registrationNumber', e.target.value)}
          />
          {/*
            Disabled inputs rather than plain text, so the panel reads as one
            grid of fields. Changing the base currency reshuffles the
            additional-currency list and the bank accounts denominated in it —
            a finance operation with consequences in every other app, not a
            field on this screen.
          */}
          <TextInput
            label="Base Currency"
            value={currencyLabel(organisation.currencyIsoCode)}
            disabled
            readOnly
          />
        </div>

        {/* ── Column 3 — assigned by the platform, plus formatting ─────────
            Full height so Save can be pushed to the bottom: grid children
            stretch to the tallest column, which is the one ending in Base
            Currency. */}
        <div className="flex h-full flex-col space-y-3 text-sm">
          <TextInput label="Network ID" value={organisation.networkId ?? ''} disabled readOnly />

          <RegionalSettingsInlineEditor
            label="Regional Settings"
            value={{
              operationalTimeZone: form.operationalTimeZone,
              languageCode: form.languageCode,
              timePattern: form.timePattern,
              datePattern: form.datePattern,
            }}
            onChange={(next) => setForm((f) => (f ? { ...f, ...next } : f))}
            timeZoneOptions={TIMEZONE_OPTIONS}
            languageOptions={LANGUAGE_OPTIONS}
            timePatternOptions={TIME_PATTERN_OPTIONS}
            datePatternOptions={DATE_PATTERN_OPTIONS}
          />

          {/* `mt-auto` drops it to the bottom of the column, level with the
              last field in the tallest one — rather than trailing whichever
              field happens to sit above it. */}
          <div className="mt-auto flex justify-end">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </FormSection>
    </form>
  );
}

